import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dimensions, FlatList, Keyboard, Platform, StyleSheet, TouchableOpacity, View } from 'react-native';
import { Theme, useTheme } from '@rneui/themed';
import { ZenEmptyComponent, ZenIcon, ZenSearchBar, ZenText } from 'zen-core-elements';

import { LocationItem, LocationPickerModalProps } from './listToolbar.types';
import SellscreenModal from '../../../components/newSellscreen/sellscreenModal';

/**
 * A fully self-contained location-picker modal.
 *
 * The parent only needs to:
 *  - maintain `locationList` state (with a `selected` flag)
 *  - call `onSelect` to update its own selection + close the modal
 *  - call `onClose` when the user dismisses without selecting
 */
const LocationPickerModal: React.FC<LocationPickerModalProps> = ({
    visible,
    onClose,
    locationList,
    onSelect,
    title = 'Select Location',
}) => {
    // Use screen dimensions (not window) so that keyboard open/close on Android
    // never changes the values and never triggers a container re-layout.
    // On web 'screen' is the whole monitor, so a short browser window cut the
    // list off - use the window there (no on-screen keyboard to worry about).
    const { width, height } = Dimensions.get(Platform.OS === 'web' ? 'window' : 'screen');

    // useTheme hook — single subscription for the whole component tree, avoiding
    // per-item ThemeConsumer wrappers that each cause an independent re-render.
    const { theme } = useTheme();

    // Memoize styles so they are only rebuilt when the theme object reference changes.
    const styles = useMemo(() => locationPickerStyle(theme), [theme]);

    // Stable container dimensions derived from fixed screen size.
    const containerStyle = useMemo(() => ({
        maxWidth: width > 900 ? width * 0.30 : width > 600 ? width * 0.60 : width * 0.80,
        maxHeight: width > 900 ? height * 0.60 : height * 0.40,
    }), [width, height]);

    const [searchText, setSearchText] = useState('');
    const [displayList, setDisplayList] = useState<LocationItem[]>(locationList ?? []);
    const scrollRef = useRef<FlatList>(null);

    // Sync display list whenever the master list or search changes
    useEffect(() => {
        if (!locationList) return;
        if (searchText.trim()) {
            const lower = searchText.toLowerCase();
            setDisplayList(locationList.filter(i => i.locationName.toLowerCase().includes(lower)));
        } else {
            setDisplayList(locationList);
        }
    }, [locationList, searchText]);

    // When the modal opens scroll to the selected item; reset search on close.
    useEffect(() => {
        if (!visible) {
            setSearchText('');
            return;
        }
        const idx = displayList.findIndex(i => i.selected);
        if (idx > 0) {
            setTimeout(() => {
                scrollRef.current?.scrollToIndex({ index: idx, animated: true });
            }, 400);
        }
    }, [visible]);

    const handleSelect = useCallback(
        (item: LocationItem) => {
            Keyboard.dismiss();
            onSelect(item);
            setSearchText('');
        },
        [onSelect],
    );

    const handleClose = useCallback(() => {
        Keyboard.dismiss();
        setSearchText('');
        onClose();
    }, [onClose]);

    // renderItem uses theme/styles from the outer closure — no nested ThemeConsumer,
    // so FlatList items do not each independently re-subscribe to the theme context.
    const renderItem = useCallback(({ item }: { item: LocationItem }) => {
        const isSelected = !!item.selected;
        return (
            <TouchableOpacity
                activeOpacity={0.7}
                onPress={() => handleSelect(item)}
                style={[styles.card, isSelected && styles.cardSelected]}
            >
                {/* Left Icon */}
                <View style={[styles.iconContainer, isSelected && styles.iconContainerSelected]}>
                    <ZenIcon
                        name='map-pin'
                        type='feather'
                        size={20}
                        color={isSelected ? theme?.light : theme?.lightGrey}
                    />
                </View>

                {/* Text Content */}
                <View style={styles.textContainer}>
                    <ZenText
                        content={item.locationName}
                        style={[styles.locationName, isSelected && styles.textSelected]}
                        numberOfLines={1}
                        ellipsizeMode="tail"
                    />
                    <ZenText
                        content={item.timeZone ?? 'Time zone not found'}
                        style={styles.locationTimezone}
                        numberOfLines={1}
                    />
                </View>

                {/* Right Indicator (Radio / Check) */}
                <View style={[styles.radio, isSelected && styles.radioSelected]}>
                    {isSelected && (
                        <ZenIcon name="check" type='feather' size={14} color={theme?.light} />
                    )}
                </View>
            </TouchableOpacity>
        );
    }, [handleSelect, styles, theme]);

    return (
        <SellscreenModal
            isModalOpen={visible}
            onCloseRequest={handleClose}
        >
            <View style={containerStyle}>
                {/* ── Header ───────────────────────────────────── */}
                <View style={styles.header}>
                    <View>
                        <ZenText content={title} style={styles.title} />
                        <ZenText content={"Where is this sale taking place?"} style={styles.subtitle} />
                    </View>
                    <TouchableOpacity onPress={handleClose} style={styles.closeButton}>
                        <ZenIcon name='close' type='antdesign' size={20} color={theme?.disabledGray500} />
                    </TouchableOpacity>
                </View>

                {/* ── Search ───────────────────────────────────── */}
                <View style={styles.searchContainer}>
                    <ZenSearchBar
                        placeholder="Search location"
                        search={searchText}
                        setSearch={setSearchText}
                        searchContainerStyle={styles.searchInputContainer}
                    />
                </View>

                {/* ── List ─────────────────────────────────────── */}
                <FlatList
                    ref={scrollRef}
                    data={displayList}
                    renderItem={renderItem}
                    keyExtractor={(item) => item.id?.toString()}
                    contentContainerStyle={styles.listContent}
                    showsVerticalScrollIndicator={false}
                    keyboardShouldPersistTaps="handled"
                    ItemSeparatorComponent={() => <View style={{ height: 12 }} />}
                    ListEmptyComponent={
                        <ZenEmptyComponent viewStyle={{ flex: 1, justifyContent: 'center', alignItems: 'center' }} />
                    }
                    onScrollToIndexFailed={(info) => {
                        setTimeout(() => {
                            scrollRef.current?.scrollToIndex({
                                index: info.index,
                                animated: true,
                            });
                        }, 100);
                    }}
                />
            </View>
        </SellscreenModal>
    );
};

const locationPickerStyle = (theme: Theme) =>
    StyleSheet.create({
        header: {
            width: '100%',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 16,
            gap: 15,
        },
        title: {
            fontSize: theme?.smallerFontSize,
            fontFamily: theme?.bold,
            color: theme?.lightDark,
            letterSpacing: -0.5,
        },
        subtitle: {
            fontSize: theme?.badgeFontSize,
            fontFamily: theme?.medium,
            color: theme?.grey,
            marginTop: 4,
        },
        closeButton: {
            padding: 4,
            backgroundColor: theme?.softSlate,
            borderRadius: 20,
        },
        searchContainer: {
            marginBottom: 16,
        },
        searchInputContainer: {
            height: 44,
            backgroundColor: theme?.lightBackgroundColor,
            borderRadius: 10,
        },
        listContent: {
            paddingBottom: 20,
            flexGrow: 1,
        },
        card: {
            flexDirection: 'row',
            alignItems: 'center',
            padding: 16,
            borderRadius: 16,
            backgroundColor: theme?.light,
            borderWidth: 1.5,
            borderColor: theme?.coolGray,
        },
        cardSelected: {
            borderColor: theme?.draggerIcon,
            backgroundColor: theme?.blue50,
        },
        iconContainer: {
            width: 40,
            height: 40,
            borderRadius: 20,
            backgroundColor: theme?.softSlate,
            justifyContent: 'center',
            alignItems: 'center',
            marginRight: 16,
        },
        iconContainerSelected: {
            backgroundColor: theme?.draggerIcon,
        },
        textContainer: {
            flex: 1,
        },
        locationName: {
            fontSize: theme?.smallFontSize,
            fontFamily: theme?.medium,
            color: theme?.lightDark,
            marginBottom: 2,
        },
        textSelected: {
            fontFamily: theme?.bold,
            color: theme?.lightDark,
        },
        locationTimezone: {
            fontSize: theme?.miniFontSize,
            fontFamily: theme?.medium,
            color: theme?.grey,
        },
        radio: {
            width: 24,
            height: 24,
            borderRadius: 12,
            borderWidth: 2,
            borderColor: theme?.coolGray,
            justifyContent: 'center',
            alignItems: 'center',
        },
        radioSelected: {
            borderColor: theme?.tealGreen,
            backgroundColor: theme?.tealGreen,
        },
    });

export default LocationPickerModal;
